import { motion } from "motion/react";
import {
  Target, Ruler, ArrowRight, Eye, FileText, X, CircleHelp,
  ChevronRight, Sparkles, GitFork,
} from "lucide-react";
import type { StrainGraph, GraphNode, GraphEdge } from "@/types";
import { useStore } from "@/store/useStore";
import { nodeMeta, provenanceMeta, edgeMeta } from "@/lib/vocabulary";
import { Badge } from "@/components/Badge";
import { StanceSlider } from "@/components/StanceSlider";
import { cn } from "@/lib/cn";

// spine view: linear reading mode of the main load-bearing path.
// mobile-first — makes the strain readable even when the graph is too large.

const ROLE_LABELS: Record<string, { label: string; icon: typeof Target; desc: string }> = {
  thesis: { label: "Main thesis", icon: Target, desc: "The core claim the strain is making." },
  criterion: { label: "Key criterion", icon: Ruler, desc: "What 'good' or 'worth it' means here." },
  warrant: { label: "Hidden warrant", icon: Eye, desc: "An unstated assumption the argument depends on." },
  support: { label: "Main support", icon: ArrowRight, desc: "The strongest reason for the thesis." },
  evidence: { label: "Key evidence", icon: FileText, desc: "The most important source or data point." },
  rebuttal: { label: "Biggest objection", icon: X, desc: "The strongest counter to the thesis." },
  undercut: { label: "Biggest undercut", icon: ChevronRight, desc: "Attacks an inference, not a claim." },
  crux: { label: "Unresolved crux", icon: CircleHelp, desc: "Where the idea actually hinges." },
  bridge: { label: "Bridge proposal", icon: Sparkles, desc: "A reformulation both camps might accept." },
};

export function SpineView({ graph }: { graph: StrainGraph }) {
  const store = useStore();

  // build the spine: ordered sequence of load-bearing nodes by role
  const spine = buildSpine(graph);

  return (
    <div className="h-full overflow-y-auto bg-grid-fine">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">spine view</div>
          <h2 className="text-lg font-semibold">Read the idea, top to bottom</h2>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">
            The main load-bearing path of this strain. No graph navigation — just the structure, in order.
          </p>
        </div>

        <div className="space-y-3">
          {spine.map((item, i) => (
            <motion.div
              key={item.node.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <SpineCard
                node={item.node}
                role={item.role}
                roleMeta={ROLE_LABELS[item.role]}
                graph={graph}
                onSelect={() => { store.setExplorerMode("canvas"); store.selectNode(item.node.id); }}
              />
            </motion.div>
          ))}
        </div>

        {/* stance at the end of the spine */}
        <div className="mt-6">
          <StanceSlider strainId={graph.strain.id} versionId={graph.version.id} />
        </div>
      </div>
    </div>
  );
}

function SpineCard({
  node, role, roleMeta, graph, onSelect,
}: {
  node: GraphNode;
  role: string;
  roleMeta: { label: string; icon: typeof Target; desc: string };
  graph: StrainGraph;
  onSelect: () => void;
}) {
  const meta = nodeMeta(node.type);
  const prov = provenanceMeta(node.provenance);
  const isInferred = node.provenance === "ai_inferred";
  const RIcon = roleMeta.icon;

  return (
    <div
      className={cn(
        "rounded-2xl border bg-[var(--color-surface)] p-4 transition-all hover:border-[var(--color-primary)] cursor-pointer",
        role === "thesis" ? "border-[var(--color-primary)] shadow-glow-primary" : "border-[var(--color-border)]",
      )}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
          style={{ background: `color-mix(in srgb, ${meta.color} 14%, transparent)`, color: meta.color }}
        >
          <RIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">{roleMeta.label}</div>
          <div className="text-[11px] text-[var(--color-ink-faint)]">{roleMeta.desc}</div>
        </div>
        <Badge tone={isInferred ? "inferred" : "muted"} icon={<prov.icon size={10} />}>{prov.short}</Badge>
      </div>

      <div className="prose-idea text-[15px] text-[var(--color-ink)] leading-relaxed">{node.text}</div>

      {node.original_text && node.original_text !== node.text && (
        <div className="mt-2 text-[12px] text-[var(--color-ink-muted)] italic font-serif border-l-2 border-[var(--color-border)] pl-2">
          “{node.original_text}”
        </div>
      )}

      {node.crux && (
        <div className="mt-3 rounded-lg border border-[rgba(251,191,36,0.25)] bg-[rgba(251,191,36,0.05)] p-2.5">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-secondary)] mb-0.5">why this is a crux</div>
          <p className="text-[12px] text-[var(--color-ink-soft)] leading-relaxed">{node.crux.reason}</p>
        </div>
      )}

      {node.stats && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[var(--color-border)] text-[11px] font-mono text-[var(--color-ink-muted)]">
          {typeof node.stats.agree === "number" && <span>agree {node.stats.agree}%</span>}
          <span>· {node.stats.comments} comments</span>
          <span>· {node.stats.forks} forks</span>
        </div>
      )}
    </div>
  );
}

function buildSpine(graph: StrainGraph): { node: GraphNode; role: string }[] {
  const { nodes, edges } = graph;
  const out: { node: GraphNode; role: string }[] = [];

  const find = (pred: (n: GraphNode) => boolean) => nodes.find(pred);
  const byBadge = (badge: string) => find((n) => !!n.badges?.includes(badge));

  // 1. thesis
  const thesis = byBadge("thesis") ?? nodes[0];
  if (thesis) out.push({ node: thesis, role: "thesis" });

  // 2. criterion
  const criterion = find((n) => n.type === "criterion" && n.id !== thesis?.id);
  if (criterion) out.push({ node: criterion, role: "criterion" });

  // 3. main support
  const supportEdge = edges.find((e) => (e.relation === "supports" || e.relation === "evidences") && e.target === thesis?.id);
  const support = supportEdge ? nodes.find((n) => n.id === supportEdge.source) : undefined;
  if (support) out.push({ node: support, role: "support" });

  // 4. hidden warrant
  const warrant = find((n) => n.type === "hidden_warrant" && n.id !== thesis?.id && n.id !== criterion?.id);
  if (warrant) out.push({ node: warrant, role: "warrant" });

  // 5. evidence
  const evidence = find((n) => (n.type === "evidence" || n.type === "empirical_claim") && n.id !== support?.id);
  if (evidence) out.push({ node: evidence, role: "evidence" });

  // 6. biggest objection (rebuttal)
  const rebutEdge = edges.find((e) => e.relation === "rebuts" && e.target === thesis?.id);
  const rebuttal = rebutEdge ? nodes.find((n) => n.id === rebutEdge.source) : undefined;
  if (rebuttal) out.push({ node: rebuttal, role: "rebuttal" });

  // 7. biggest undercut
  const undercutEdge = edges.find((e) => e.relation === "undercuts");
  const undercut = undercutEdge ? nodes.find((n) => n.id === undercutEdge.source) : undefined;
  if (undercut) out.push({ node: undercut, role: "undercut" });

  // 8. unresolved crux
  const crux = byBadge("crux") ?? find((n) => !!n.crux && n.id !== thesis?.id);
  if (crux && !out.find((o) => o.node.id === crux.id)) out.push({ node: crux, role: "crux" });

  // 9. bridge
  const bridge = find((n) => n.type === "bridge");
  if (bridge) out.push({ node: bridge, role: "bridge" });

  return out;
}
